import { Checkbox } from 'module/ui/components/atoms/checkbox';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Loading } from 'module/ui/components/atoms/loading';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const SignalsDropdownMenu = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0px 0.6px 1.8px 0px rgba(0, 0, 0, 0.11),
    0px 3.2px 7.2px 0px rgba(0, 0, 0, 0.13);
  padding: 8px 0;
  min-width: 260px;
`;
const SignalsList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`;

const DropdownHeader = styled.div`
  padding: 8px;
  font-size: 12px;
  color: #6f6f6f;
  line-height: 16px;
  letter-spacing: 0px;
  font-weight: 600;
`;

interface SignalRowProps {
  selected: boolean;
}

const SignalRow = styled.div<SignalRowProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  background: ${({ selected }) => (selected ? '#D0E2FF' : '#fff')};
  border-radius: 4px;
  font-size: 14px;
  color: #161616;
  margin: 2px 0px;
  transition: background 0.2s;
  &:hover {
    background: #f4f4f4;
  }
  .signal-name {
    flex: 1;
  }
`;

const StyledSearchInput = styled(SearchInputExpanded)`
  input {
    padding: 7px 12px;
    border-radius: 8px;
    border: 1px solid #f2f2f2;
    font-size: 14px;
    color: #161616;
    &::placeholder {
      color: #a8a8a8;
      opacity: 1;
    }
  }
`;

interface Props {
  value?: number[];
  onChange?: (value: number[]) => void;
}

export const SignalSelector = ({ value, onChange }: Props) => {
  const { data: signalConfigurations, isLoading } =
    useGetAllSignalConfigurationsQuery();
  const {
    value: search,
    onChange: setSearch,
    clear: clearSearch,
  } = useSearchValueState();
  const handleSignalToggle = useCallback(
    (id: number) => {
      const newSelectedSignals = value?.includes(id)
        ? value?.filter((s) => s !== id)
        : [...(value ?? []), id];

      onChange?.(newSelectedSignals);
    },
    [onChange, value],
  );
  const isSomeSignalSelected = useMemo(() => {
    return (
      value?.length &&
      value.length > 0 &&
      value.length !== signalConfigurations?.length
    );
  }, [value, signalConfigurations]);

  return (
    <Dropdown
      overlay={
        <SignalsDropdownMenu>
          <DropdownHeader>
            <FormattedMessage id="all_signals.dropdown.title" />
          </DropdownHeader>
          <StyledSearchInput
            placeholder="Search"
            value={search}
            onChange={setSearch}
            onClear={() => clearSearch()}
          />
          <SignalsList>
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {signalConfigurations?.map((signal: any) => (
                  <SignalRow
                    key={signal.signalConfigurationId}
                    selected={
                      value?.includes(signal.signalConfigurationId) ?? false
                    }
                    onClick={() =>
                      handleSignalToggle(signal.signalConfigurationId)
                    }
                  >
                    <Checkbox
                      checked={
                        value?.includes(signal.signalConfigurationId) ?? false
                      }
                      onChange={() =>
                        handleSignalToggle(signal.signalConfigurationId)
                      }
                    />
                    <span className="signal-name">{signal.signalName}</span>
                  </SignalRow>
                ))}
              </>
            )}
          </SignalsList>
        </SignalsDropdownMenu>
      }
      trigger={['click']}
    >
      <Button leftIcon={<FontAwesomeIcon icon={faPlus} />}>
        {isSomeSignalSelected ? (
          <FormattedMessage
            id="all_signals.dropdown.some_selected"
            values={{ count: value?.length ?? 0 }}
          />
        ) : (
          <FormattedMessage id="all_signals.dropdown.all_signals" />
        )}
      </Button>
    </Dropdown>
  );
};
